import {
	type OrderDetailResponseDto,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { useResource } from "Infrastructure/Translation/Resources";
import { OrderComments } from "Order/Components/Comments/OrderComments";
import { OrderInfo } from "Order/Components/Detail/Info";
import { useOrderEditForm } from "Order/Components/Form/Hooks/useOrderEditForm";
import { AssignPartnerCardWidget } from "Order/Widgets/AssignPartnerCardWidget";
import { DateNextActionCardWidget } from "Order/Widgets/DateNextActionCardWidget";
import { OrderAttachmentsWidget } from "Order/Widgets/OrderAttachmentsWidget";
import { OrderTimelineWidget } from "Order/Widgets/OrderTimelineWidget";
import { PartnerAllocationHistoryWidget } from "Order/Widgets/PartnerAllocationHistoryWidget";
import { StatusCardWidget } from "Order/Widgets/StatusCardWidget";
import { ExpandCollapseButton } from "Shared/Components/Animation/ExpandCollapseAnimation";
import { CLAIM_STATUS_ACTIVE } from "Shared/consts";
import Save from "@mui/icons-material/Save";
import { Button, Fab, Grid2 } from "@mui/material";
import { FormProvider } from "react-hook-form";

export type OrderDetailTabProps = {
	order: OrderDetailResponseDto;
};

export function OrderDetailTab({ order }: OrderDetailTabProps) {
	const { t } = useResource();
	const isActiveClaim = order.claims.some(
		(claim) => claim.status === CLAIM_STATUS_ACTIVE,
	);

	const {
		form,
		handleSubmitUpdate: handleSubmitOrder,
		isPending,
		statusValue,
	} = useOrderEditForm(order, t);
	const isStatusChangeBlocked =
		isActiveClaim && statusValue === OrderStatus.done;

	const handleSubmit = () => {
		form.formState.isDirty && handleSubmitOrder();
	};

	return (
		<>
			<Grid2 container spacing={2} direction={{ xs: "column", md: "row" }}>
				<Grid2
					container
					size={{ xs: 12, sm: 12, md: 8 }}
					direction="column"
					alignItems="flex-start"
				>
					<Grid2 size={{ xs: 12 }}>
						{order.market && (
							<OrderTimelineWidget orderId={order.id} market={order.market} />
						)}
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<PartnerAllocationHistoryWidget orderId={order.id} />
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<OrderAttachmentsWidget orderId={order.id} />
					</Grid2>
					<Grid2 size={{ xs: 12 }}>
						<OrderComments id={order.id} />
					</Grid2>
				</Grid2>

				<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
					<FormProvider {...form}>
						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }}>
								<StatusCardWidget
									status={statusValue}
									storedStatus={order.status}
									isDisabled={order.sfInvoicePeriod !== null}
									order={order}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<DateNextActionCardWidget />
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<AssignPartnerCardWidget order={order} />
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<OrderInfo data={order} />
							</Grid2>
						</Grid2>
					</FormProvider>
				</Grid2>
			</Grid2>
			<ExpandCollapseButton showElement={form.formState.isDirty}>
				<Fab
					component={Button}
					color="primary"
					loading={isPending}
					onClick={handleSubmit}
					disabled={isStatusChangeBlocked}
				>
					{!isPending && <Save fontSize="large" />}
				</Fab>
			</ExpandCollapseButton>
		</>
	);
}
